import { useLocation, Link } from "react-router-dom";

import {
  Box,
  IconButton,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Icon,
  Spacer,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiChevronRight, FiHome, FiMapPin, FiMenu } from "react-icons/fi";
import { useUserData } from "../data/userContext";
import { BrandMark } from "./ui";

interface HeaderProps {
  showSidebarButton?: boolean;
  onShowSidebar: () => void;
}

/** Humanised route labels for the breadcrumb eyebrow; unknown segments fall back to "Partner Portal". */
const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  contracts: "My Contracts",
  tickets: "Help and Support",
};

const routeLabel = (segment: string) => ROUTE_LABELS[segment] ?? "Partner Portal";

const Header = ({
  showSidebarButton = true,
  onShowSidebar,
}: HeaderProps): JSX.Element => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const userData = useUserData();
  const branchName = userData?.subsidiary.text;

  return (
    <Flex
      as="header"
      h="64px"
      flexShrink={0}
      position="sticky"
      top={0}
      zIndex="sticky"
      layerStyle="glass"
      className="mbf-glass"
      px={{ base: 4, md: 6, xl: 8 }}
      alignItems="center"
      gap={3}
    >
      {showSidebarButton && (
        <IconButton
          isRound
          variant="ghost"
          aria-label="Open navigation"
          icon={<FiMenu size="1.25rem" />}
          onClick={onShowSidebar}
          ml={-2}
        />
      )}

      <Breadcrumb
        minW={0}
        overflow="hidden"
        whiteSpace="nowrap"
        // Room inside the clip box for the 4px focus ring on the links, cancelled out so the layout does not move
        p={1.5}
        m={-1.5}
        separator={<Icon as={FiChevronRight} boxSize="12px" display="block" aria-hidden />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/" display="inline-flex" alignItems="center">
            <Icon as={FiHome} boxSize="14px" aria-hidden />
            <VisuallyHidden>Home</VisuallyHidden>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {pathnames.length === 0 && (
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{routeLabel("")}</BreadcrumbLink>
          </BreadcrumbItem>
        )}
        {pathnames.map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;

          return (
            <BreadcrumbItem key={to} isCurrentPage={isLast}>
              {isLast ? (
                <BreadcrumbLink>{routeLabel(value)}</BreadcrumbLink>
              ) : (
                <BreadcrumbLink as={Link} to={to}>
                  {routeLabel(value)}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
      <Spacer />

      <HStack spacing={3} flexShrink={0}>
        {branchName && (
          <HStack
            as="span"
            spacing={1.5}
            display={{ base: "none", md: "inline-flex" }}
            h="28px"
            px={3}
            borderRadius="full"
            bg="navy.50"
            color="navy.700"
            maxW="18rem"
            title={branchName}
          >
            <Icon as={FiMapPin} boxSize="14px" aria-hidden />
            <Text as="span" textStyle="caption" fontWeight={700} noOfLines={1}>
              {branchName}
            </Text>
          </HStack>
        )}
        <Box display={{ base: "none", sm: "block" }}>
          <BrandMark variant="light" h="28px" />
        </Box>
      </HStack>
    </Flex>
  );
};

export default Header;
